<script lang="ts">
  import { storyblokEditable } from "@storyblok/svelte";
  import { page } from "$app/stores";
  import { deviceDetails } from "$lib/consumer/stores/deviceDetails";
  import GetDeliveryButton from "./GetDeliveryButton.svelte";
  import { onMount } from "svelte";

  // Get the block data
  export let blok;

  // Set the debug flags
  $: novideo = $page.url.searchParams.get("novideo") === "1";

  // Get the device details
  $: ({ isMobile } = $deviceDetails);

  // Get the placeholder image source (same for desktop and mobile)
  let placeholderSrc = blok.videoPlaceholder.filename;

  // Reference for the video file source (different for desktop and mobile)
  let videoSrc = "";

  // Track if the component is mounted
  let isMounted = false;

  // Track if the video is visible in the viewport
  let videoIsVisible = false;

  // Autoplay on load for header videos only
  let autoplayOnLoad = blok.type === "header";

  // Element references
  let video: HTMLVideoElement;
  let videoWrapper: HTMLDivElement;

  onMount(() => {
    // Set video source based on device type
    videoSrc = isMobile ? blok.videoMobile.filename : blok.videoDesktop.filename;
    isMounted = true;

    // Check if the video is visible in the viewport
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      videoIsVisible = entry.isIntersecting;

      // Toggle video playback (for videos that don't autoplay on load)
      if (video && !autoplayOnLoad) {
        if (videoIsVisible) {
          video.play();
        } else {
          video.pause();
        }
      }
    });

    if (videoWrapper) {
      observer.observe(videoWrapper);
    }

    // Unmount
    return () => {
      if (videoWrapper) {
        observer.unobserve(videoWrapper);
      }
    };
  });
</script>

<section class="hero-video-section mx-auto" use:storyblokEditable={blok}>
  <div class="video-wrapper" bind:this={videoWrapper}>
    {#if novideo === true}
      <!-- Show image placeholder if video debug flag is enabled -->
      <img class="hero-video" src={placeholderSrc} alt="Zip making a delivery" />
    {:else if isMounted}
      <!-- Show video only after component is mounted -->
      <video
        class="hero-video"
        src={videoSrc}
        poster={placeholderSrc}
        autoplay={autoplayOnLoad}
        muted
        loop
        playsinline
        bind:this={video}
      />
    {:else}
      <!-- Show poster image until component is mounted -->
      <img class="hero-video" src={placeholderSrc} alt="Zip making a delivery" />
    {/if}

    <div class="video-overlay section-header-width type-{blok.type}">
      <div class="video-overlay-text">
        <h1
          class="main-heading"
          style="max-width: {blok.contentMaxWidth ? blok.contentMaxWidth + 'px' : 'none'};"
        >
          {@html blok.heading}
        </h1>

        <div class="subheading-and-button-wrapper">
          <h2
            class="main-subheading"
            style="max-width: {blok.contentMaxWidth ? blok.contentMaxWidth + 'px' : 'none'};"
          >
            {@html blok.subheading}
          </h2>

          <GetDeliveryButton {blok} />
        </div>
      </div>
    </div>
  </div>
</section>

<style lang="postcss">
  .video-wrapper {
    position: relative;
    width: 100%;
    height: 90vh;
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    background-color: #f2f1ef;

    @screen md {
      height: 600px;
    }

    @screen lg {
      height: 85vh;
    }

    @media (min-width: 2000px) {
      margin-left: auto;
      margin-right: auto;
    }
  }

  .hero-video {
    width: 100vw;
    height: 90vh;
    object-fit: cover;
    background: #f2f1ef;

    @screen md {
      height: 600px;
    }

    @screen lg {
      height: 85vh;
    }
  }

  /* Header */

  .video-overlay.type-header {
    position: absolute;
    width: 100%;
    bottom: 0;
    display: flex;
    flex-direction: column;
    margin-bottom: 2rem;

    @screen md {
      margin-bottom: 2.8rem;
    }

    @screen lg {
      width: 100%;
      margin-bottom: 3.5rem;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  .video-overlay.type-header .subheading-and-button-wrapper {
    @screen md {
      display: flex;
      margin-top: 2rem;
      gap: 2rem;
      align-items: center;
    }
  }

  /* Override the check availability button component styles */
  .video-overlay.type-header :global(.get-delivery-button-wrapper) {
    margin-top: 2rem;

    @screen md {
      margin-top: 0;
    }
  }

  /* Footer */

  .video-overlay.type-footer {
    position: absolute;
    bottom: auto;
    width: 100%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
  }

  .video-overlay.type-footer .video-overlay-text {
    max-width: 100%;
  }

  .video-overlay.type-footer .subheading-and-button-wrapper {
    margin-top: 2rem;

    @screen md {
      margin-top: 3rem;
    }
  }

  /* Override the check availability button component styles */
  .video-overlay.type-footer :global(.get-delivery-button-wrapper) {
    margin-top: 2rem;
    margin-left: auto;
    margin-right: auto;
  }

  /* Headings, styles common to both header and footer types */

  .main-heading {
    font-size: 50px;
    font-family: var(--font-secondary);
    font-weight: var(--font-weight-semibold);
    line-height: 1;
    color: #fff;
    white-space: pre-wrap;

    @screen md {
      font-size: 72px;
    }

    @screen lg {
      font-size: 88px;
    }
  }

  .main-subheading {
    margin-top: 1rem;
    font-size: 16px;
    line-height: 1.42;
    font-weight: 300;
    color: #fff;
    white-space: pre-wrap;

    @screen md {
      margin-top: 0;
      font-size: 20px;
    }
  }
</style>
